import IconButton from '@/global/components/Button/IconButton'
import { GridCellParams } from '@/global/components/DataGrid/utils/types'
import DevelopmentOnly from '@/global/components/DevelpmentOnly'
import Menu from '@/global/components/MenuV2'
import { useAuth } from '@/hooks'
import { CategoriaEnum, CategoriaID } from '@acme/auth'
import { insensitive, OrigensEnum } from '@acme/common'
import { SN, TipoOcorrencia } from '@acme/core'
import dayjs from 'dayjs'
import { hasPermission } from 'hooks/useBackendMenu'
import {
    ArrowRightLeftIcon,
    CalendarArrowDownIcon,
    CalendarClockIcon,
    CalendarMinus2Icon,
    CalendarMinusIcon,
    ClockPlusIcon,
    EllipsisVerticalIcon,
    ShieldUserIcon,
    StarIcon,
} from 'lucide-react'
import {
    Empresa,
    Funcionario,
    ocorrencia,
    registros,
    tabelaHorarios,
} from 'service/api'
import { useStore } from 'store'
import { useUpdateDigitacaoQueryData } from './pages/useDigitacaoData'

interface OpcoesDigitacaoHooks {
    categoria: CategoriaID
    funcionario: Funcionario | null
    empresa: Empresa | null
    setQueryData: ReturnType<typeof useUpdateDigitacaoQueryData>['setQueryData']
    hasPermissionOcorrencia: Map<string, boolean>
    canCreateAbonos: boolean
    canCreateCompensacaoFeriados: boolean
    canCreateCompensacaoManual: boolean
    canCreateTrocaDiaTrabalho: boolean
    canCreateFaltas: boolean
    canDeleteRegistros: boolean
    canCreateRegistros: boolean
}

export function useOpcoesDigitacaoHooks(): OpcoesDigitacaoHooks {
    const {
        decoded: { categoria },
    } = useAuth()
    const funcionario = useStore(state => state.employee)
    const empresa = useStore(state => state.company)
    const { setQueryData } = useUpdateDigitacaoQueryData()

    const canCreateAbonos = hasPermission(['create', 'Abonos'])
    const canCreateCompensacaoFeriados = hasPermission([
        'create',
        'CompensaçãoDeFeriados',
    ])
    const canCreateCompensacaoManual = hasPermission([
        'create',
        'CompensaçãoManual',
    ])
    const canCreateTrocaDiaTrabalho = hasPermission([
        'create',
        'TrocaDeDiaDeTrabalho',
    ])
    const canCreateFaltas = hasPermission([
        'create',
        'FaltasJustificadasOuInjustificadas',
    ])
    const canDeleteRegistros = hasPermission(['delete', 'Registros'])
    const canCreateRegistros = hasPermission(['create', 'Registros'])

    // Permissões para todos os tipos de ocorrência
    const canCreateAfastamento = hasPermission(['create', 'Afastamentos'])
    const canCreateCompensacao = hasPermission(['create', 'Compensação'])
    const canCreateAmamentacao = hasPermission(['create', 'Amamentação'])
    const canCreateFolga = hasPermission(['create', 'Folga'])

    const hasPermissionOcorrencia = new Map<string, boolean>([
        [TipoOcorrencia.AFASTAMENTO, canCreateAfastamento],
        [TipoOcorrencia.COMPENSACAO, canCreateCompensacao],
        [TipoOcorrencia.AMAMENTACAO, canCreateAmamentacao],
        [TipoOcorrencia.FOLGA, canCreateFolga],
    ])

    return {
        categoria: categoria as CategoriaID,
        funcionario,
        empresa,
        setQueryData,
        hasPermissionOcorrencia,
        canCreateAbonos,
        canCreateCompensacaoFeriados,
        canCreateCompensacaoManual,
        canCreateTrocaDiaTrabalho,
        canCreateFaltas,
        canDeleteRegistros,
        canCreateRegistros,
    }
}

export function OpcoesDigitacao({
    row: {
        dia,
        registros: registrosRow,
        faltas,
        horasExtra,
        compensacaoManual,
        isAtivo,
    },
    categoria,
    funcionario,
    empresa,
    setQueryData,
    hasPermissionOcorrencia,
    canCreateAbonos,
    canCreateCompensacaoFeriados,
    canCreateCompensacaoManual,
    canCreateTrocaDiaTrabalho,
    canCreateFaltas,
    canDeleteRegistros,
    canCreateRegistros,
}: GridCellParams & OpcoesDigitacaoHooks) {
    const { mutateAsync: excluirRegistro } = registros.mutations.useSoftDelete()
    const { mutateAsync: criarRegistro } = registros.mutations.useCreate()
    const { data: ocorrenciasData = [] } = ocorrencia.useFetch()

    const ocorrencias = ocorrenciasData
        .filter(({ atalho }) => SN.isTrue(atalho))
        .sort((a, b) => a.descricao.localeCompare(b.descricao))

    // Verifica se o dia tem falta ou extra
    const temFalta = faltas && faltas !== '00:00'
    const temExtra = horasExtra && horasExtra !== '00:00'

    if (!isAtivo) return <InactiveButton />

    if (categoria === CategoriaEnum.Funcionario) {
        if (SN.isTrue(funcionario?.ajustesPonto))
            return (
                <Menu.Root position="right">
                    <Menu.Trigger>
                        <IconButton tabIndex={-1}>
                            <EllipsisVerticalIcon />
                        </IconButton>
                    </Menu.Trigger>
                    <Menu.List>
                        <Menu.Item href={`abono/adicionar/${dia}`}>
                            Adicionar abono
                        </Menu.Item>
                        <Menu.Item href={`registros/adicionar/${dia}`}>
                            Adicionar registro
                        </Menu.Item>
                    </Menu.List>
                </Menu.Root>
            )
        else return <InactiveButton />
    }

    const getCompensacaoManualUrl = () => {
        const params = new URLSearchParams()
        if (temFalta) params.append('tipo', 'falta')
        if (temExtra) params.append('tipo', 'extra')
        const queryString = params.toString()
        return `compensacao-manual/adicionar/${dia}${
            queryString ? `?${queryString}` : ''
        }`
    }

    const menuItems = [
        {
            to: `abono/adicionar/${dia}`,
            label: 'Abono',
            icon: <ClockPlusIcon className="mr-2 opacity-50" />,
            show: canCreateAbonos,
        },
        {
            to: `compensacao-feriado/adicionar/${dia}`,
            label: 'Compensação de feriado',
            icon: <CalendarArrowDownIcon className="mr-2 opacity-50" />,
            show: canCreateCompensacaoFeriados,
        },
        {
            to: getCompensacaoManualUrl(),
            label: 'Compensação manual',
            icon: <CalendarClockIcon className="mr-2 opacity-50" />,
            show:
                !compensacaoManual &&
                canCreateCompensacaoManual &&
                SN.isTrue(funcionario?.RegrasCalculo?.compensacaoManual),
        },
        {
            to: `troca-dia-trabalho/adicionar/${dia}`,
            label: 'Troca dia trabalho',
            icon: <ArrowRightLeftIcon className="mr-2 opacity-50" />,
            show: canCreateTrocaDiaTrabalho,
        },
        {
            to: `falta-injustificada/adicionar/${dia}`,
            label: 'Falta injustificada',
            icon: <CalendarMinusIcon className="mr-2 opacity-50" />,
            show: canCreateFaltas,
        },
        {
            to: `falta-justificada/adicionar/${dia}`,
            label: 'Falta justificada',
            icon: <CalendarMinus2Icon className="mr-2 opacity-50" />,
            show: canCreateFaltas,
        },
        ...ocorrencias.map(ocorrencia => ({
            to: `afastamento/adicionar?ocorrenciaId=${ocorrencia.id}&dia=${dia}`,
            label: ocorrencia.descricao,
            icon: (
                <div
                    className="flex items-center"
                    title={`Atalho rápido\nDefinido na tela de ocorrências`}
                >
                    <StarIcon className="mr-2 text-yellow-400 cursor-help opacity-50" />
                </div>
            ),
            show:
                hasPermissionOcorrencia.get(ocorrencia.tipo) &&
                !ocorrencia.disabled,
        })),
    ]

    const filteredItems = menuItems.filter(item => item.show)
    if (filteredItems.length > 0 && funcionario && empresa) {
        return (
            <Menu.Root
                position="right"
                arrow
                key={`${ocorrencias.length} + ${empresa?.id}`}
            >
                <Menu.Trigger>
                    <IconButton tabIndex={-1}>
                        <EllipsisVerticalIcon />
                    </IconButton>
                </Menu.Trigger>
                <Menu.List>
                    {filteredItems.map((item, index) => (
                        <Menu.Item key={index} href={item.to}>
                            {item.icon}
                            {item.label}
                        </Menu.Item>
                    ))}
                    <DevelopmentOnly>
                        {canDeleteRegistros && (
                            <Menu.Item
                                onClick={async () =>
                                    Promise.all(
                                        registrosRow.map((registro: any) =>
                                            excluirRegistro({
                                                ...registro,
                                                motivo: 'NÃO FAÇA ISSO EM PRODUÇÃO',
                                            })
                                                .then(() =>
                                                    setQueryData(
                                                        'REGISTRO',
                                                        registro,
                                                        true,
                                                    ),
                                                )
                                                .catch(() => null),
                                        ),
                                    )
                                }
                                className="line-through"
                            >
                                <ShieldUserIcon className="mr-2 opacity-50" />
                                Limpar registros do dia
                            </Menu.Item>
                        )}

                        {canCreateRegistros && (
                            <Menu.Item
                                onClick={async () => {
                                    const tabelaDia =
                                        await tabelaHorarios.funcionario.get({
                                            funcionarioId: funcionario.id,
                                            dataInicial: dia,
                                            dataFinal: dia,
                                        })

                                    if (tabelaDia.at(0)?.TabelaHorario) {
                                        const daySemana = insensitive(
                                            dayjs
                                                .utc(dia)
                                                .locale('pt-br')
                                                .format('ddd'),
                                            'lower',
                                        )

                                        for await (const horario of Object.entries(
                                            tabelaDia[0].TabelaHorario,
                                        ).filter(
                                            ([key]) =>
                                                key.startsWith(
                                                    `${daySemana}Ent`,
                                                ) ||
                                                key.startsWith(
                                                    `${daySemana}Sai`,
                                                ),
                                        )) {
                                            const [, value] = horario as [
                                                string,
                                                string,
                                            ]

                                            if (value) {
                                                await criarRegistro({
                                                    hora: value,
                                                    data: dia,
                                                    empresaId: Number(
                                                        empresa.id,
                                                    ),
                                                    funcionarioId: Number(
                                                        funcionario.id,
                                                    ),
                                                    statusAprovacao: 'APROVADO',
                                                    origem: OrigensEnum.CONTROLE_ACESSO,
                                                    __typename: 'Registro',
                                                    entradaDiaSeguinte: 'N',
                                                    justificativa: '',
                                                })
                                                    .then(res => {
                                                        setQueryData(
                                                            'REGISTRO',
                                                            res,
                                                        )
                                                    })
                                                    .catch(() => null)
                                            }
                                        }
                                    }
                                }}
                                className="line-through"
                            >
                                <ShieldUserIcon className="mr-2 opacity-50" />
                                Preencher registros do dia
                            </Menu.Item>
                        )}
                    </DevelopmentOnly>
                </Menu.List>
            </Menu.Root>
        )
    }

    return <InactiveButton />
}

function InactiveButton() {
    return (
        <IconButton disabled tabIndex={-1}>
            <EllipsisVerticalIcon />
        </IconButton>
    )
}
